<template>
	<AppLayout :title="trans('menu.association.academic-certificates')">
		<Modal :id="modalId">
			<template #title>{{ trans('account.certificates.academic.title') }}</template>
			<LabeledObject required full>
				<template #label>{{ trans('general.select_file') }}</template>
				<input type="file" accept="application/pdf" @change="loadFile($event)">
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="upload">
					<i class="fa-solid fa-upload mr-2"/>
					{{ trans('general.upload_file') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton v-if="!pending" :color="Color.Green" @click.stop="showModal(modalId)">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<template v-if="pending">
			<Alert :color="Color.Yellow">{{ trans('account.certificates.academic.pending') }}</Alert>
			<SecondaryButton :color="Color.Red" class="place-self-start" @click.stop="cancel">
				<i class="fa-solid fa-ban mr-2"/>
				{{ trans('general.cancel') }}
			</SecondaryButton>
		</template>
		<AcademicCertificateStatus v-else-if="lastAcademicCertificate" :certificate="lastAcademicCertificate"/>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import utc from 'dayjs/plugin/utc'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import timezone from 'dayjs/plugin/timezone'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/Modal/Modal.vue'
import Alert from '@/Components/Alerts/Alert.vue'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import LabeledObject from '@/Components/LabeledObject.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/Buttons/SecondaryButton.vue'
import {computed, type ComputedRef, defineProps, type Ref, ref} from 'vue'
import {type AcademicCertificate} from '@/Types/Tools/AcademicCertificate'
import AcademicCertificateStatus from '@/Components/Certificates/Academic/AcademicCertificateStatus.vue'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors, showWarning} from '@/helpers'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.tz.setDefault('America/Argentina/Buenos_Aires')

const props = defineProps<{
	lastAcademicCertificate:AcademicCertificate|null,
}>()

const working:Ref<boolean> = ref(false)
const modalId:Ref<string> = ref('create-academic-certificate-modal')
const newCertificate:Ref<AcademicCertificate> = ref({
	file: null,
} as AcademicCertificate)
const lastAcademicCertificate:Ref<AcademicCertificate|null> = ref(props.lastAcademicCertificate)

const pending:ComputedRef<boolean> = computed(() => {
	return lastAcademicCertificate.value !== null && lastAcademicCertificate.value.valid === null
})

function loadFile(event: Event):void{
	if(event.target && event.target instanceof HTMLInputElement && event.target.files){
		newCertificate.value.file = event.target.files[0]
	}
}

function upload():void{
	if(!working.value){
		if(!newCertificate.value.file){
			showWarning(trans('account.messages.certificates.academic.missing'))
		} else {
			working.value = true
			showInfo(trans('account.messages.certificates.academic.uploading'))
			let data = new FormData
			data.append('file', newCertificate.value.file ?? '')
			axios.post(route('association.academic-certificates.store'), data, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}).then(({data}) => {
				lastAcademicCertificate.value = data
				closeModal(modalId.value)
				showSuccess(trans('account.messages.certificates.academic.uploaded'))
			}).catch(showValidationErrors).finally(() => {
				working.value = false
			})
		}
	}
}

function cancel():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('account.messages.certificates.academic.canceling'))
		axios.delete(route('association.academic-certificates.destroy')).then(({data}) => {
			lastAcademicCertificate.value = data
			showSuccess(trans('account.messages.certificates.academic.canceled'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>